import { GuestModel } from 'api-hooks/guest';
import {
  CategoryTypeEnum,
  GenderTypeEnum,
  GuestRelationTypeEnum,
} from 'common/constants/enum';
import { PreferenceFormType } from 'common/form-schema';
import { generateId } from 'common/utils/string';
import React from 'react';

import {
  GuestAirlineFormType,
  GuestApecCardFormType,
  GuestFormType,
  GuestInsuranceFormType,
  GuestOtherFormType,
  GuestPassportFormType,
  GuestRelationFormType,
  GuestVaccineFormType,
  GuestVisaFormType,
} from '../components/form-type';

export function onAddNewGuestPassport(): GuestPassportFormType {
  return {
    id: undefined,
    dateOfBirth: null,
    expiredAt: null,
    familyName: '',
    files: [],
    givenName: '',
    identifierNumber: '',
    issueState: '',
    nationality: '',
    number: '',
    placeOfBirth: '',
    type: '',
    gender: GenderTypeEnum.Male,
  };
}

export function onAddNewGuestApecCard(): GuestApecCardFormType {
  return {
    applicationNumber: '',
    email: '',
    expiredAt: null,
    files: [],
    name: '',
    number: '',
    passportNumber: '',
    pin: '',
    type: '',
    id: undefined,
  };
}

export function onAddNewGuestAirline(): GuestAirlineFormType {
  return {
    airlineId: '',
    customId: generateId(),
    memberNumber: '',
    note: '',
    password: '',
    id: undefined,
  };
}

export function onAddNewGuestRelation(): GuestRelationFormType {
  return {
    customId: generateId(),
    guestId: '',
    type: GuestRelationTypeEnum.MainFamily,
    id: undefined,
  };
}

export function onAddNewGuestVisa(): GuestVisaFormType {
  return {
    customId: generateId(),
    expiredAt: null,
    files: [],
    issuedAt: null,
    number: '',
    visaDocumentId: '',
    id: undefined,
  };
}

export function onAddNewGuestInsurance(): GuestInsuranceFormType {
  return {
    // code: '',
    customId: generateId(),
    expiredAt: null,
    files: [],
    issuedAt: null,
    number: '',
    // preferenceId: '',
    type: '',
    id: undefined,
  };
}

export function onAddNewGuestVaccine(): GuestVaccineFormType {
  return {
    files: [],
    customId: generateId(),
    id: undefined,
  };
}

export function onAddNewGuestOther(): GuestOtherFormType {
  return {
    description: '',
    customId: generateId(),
    files: [],
    name: '',
    id: undefined,
  };
}

export default function useGetDefaultValues(guest?: GuestModel): GuestFormType {
  const defaultValues = React.useMemo<GuestFormType>(() => {
    const guestPassport = guest?.passports;
    const guestApecCard = guest?.apecCard;

    const hasPassport = guest?.hasPassport || false;
    const hasApecCard = guest?.hasApecCard || false;

    const airlines: GuestAirlineFormType[] = (guest?.airlines || []).map(
      (airline) => {
        return {
          airlineId: airline?.airline?.id || '',
          customId: generateId(),
          id: airline.id,
          memberNumber: airline.memberNumber || '',
          note: airline.note || '',
          password: airline.password || '',
        };
      },
    );

    const insurances: GuestInsuranceFormType[] = (guest?.insurances || []).map(
      (insurance) => {
        return {
          number: insurance.number || '',
          customId: generateId(),
          expiredAt: insurance.expiredAt || null,
          id: insurance.id || '',
          issuedAt: insurance.issuedAt || null,
          type: insurance.type || '',
          files: (insurance.files || []).map((file) => {
            return {
              id: file.id,
              file: file.name,
              url: file.url,
            };
          }),
          // code: '',
          // preferenceId: '',
        };
      },
    );

    const relations: GuestRelationFormType[] = (guest?.relations || []).map(
      (relation) => {
        return {
          customId: generateId(),
          guestId: relation.id,
          id: relation.id,
          type: relation.type,
        };
      },
    );

    const others: GuestOtherFormType[] = (guest?.others || []).map((other) => {
      return {
        customId: generateId(),
        description: other.description || '',
        files: (other.files || []).map((file) => {
          return {
            id: file.id,
            file: file.name,
            url: file.url,
          };
        }),
        id: other.id,
        name: other.name,
      };
    });

    const preferences: PreferenceFormType[] = (guest?.preferences || []).map(
      (preference) => {
        return {
          id: preference.id,
          label: preference.name,
        };
      },
    );

    const vaccines: GuestVaccineFormType[] = (guest?.vaccines || []).map(
      (vaccine) => {
        return {
          customId: generateId(),
          files: vaccine.files.map((file) => {
            return {
              id: file.id,
              file: file.name,
              url: file.url,
            };
          }),
          id: vaccine.id,
        };
      },
    );

    const visas: GuestVisaFormType[] = (guest?.visas || []).map((visa) => {
      return {
        customId: generateId(),
        expiredAt: visa.expiredAt || null,
        id: visa.id,
        issuedAt: visa.issuedAt || null,
        number: visa.number || '',
        visaDocumentId: visa?.visaDocument?.id || '',
        files: visa.files.map((file) => {
          return {
            id: file.id,
            file: file.name,
            url: file.url,
          };
        }),
      };
    });

    const apecCard: GuestApecCardFormType | null = hasApecCard
      ? {
          id: guestApecCard?.id,
          expiredAt: guestApecCard?.expiredAt || null,
          passportNumber: guestApecCard?.passportNumber || '',
          pin: guestApecCard?.pin || '',
          email: guestApecCard?.email || '',
          applicationNumber: guestApecCard?.applicationNumber || '',
          name: guestApecCard?.name || '',
          number: guestApecCard?.number || '',
          type: guestApecCard?.type || '',
          files: (guestApecCard?.files || []).map((file) => {
            return {
              id: file.id,
              file: file.name,
              url: file.url,
            };
          }),
        }
      : null;

    const passport: GuestPassportFormType | null = hasPassport
      ? {
          id: guestPassport?.id,
          dateOfBirth: guestPassport?.dateOfBirth || null,
          expiredAt: guestPassport?.expiredAt || null,
          familyName: guestPassport?.familyName || '',
          gender: guestPassport?.gender || '',
          givenName: guestPassport?.givenName || '',
          identifierNumber: guestPassport?.identifierNumber || '',
          issueState: guestPassport?.issueState || '',
          nationality: guestPassport?.nationality || '',
          number: guestPassport?.number || '',
          placeOfBirth: guestPassport?.placeOfBirth || '',
          type: guestPassport?.type || '',
          files: (guestPassport?.files || []).map((file) => {
            return {
              id: file.id,
              file: file.name,
              url: file.url,
            };
          }),
        }
      : null;

    return {
      airlines,
      insurances,
      relations,
      others,
      preferences,
      vaccines,
      visas,
      data: guest,
      apecCard,
      passport,
      hasPassport,
      hasApecCard,
      alias: guest?.alias || '',
      birthDate: guest?.birthDate || null,
      birthPlace: guest?.birthPlace || '',
      category: guest?.category || CategoryTypeEnum.Regular,
      email: guest?.email || '',
      gender: guest?.gender || GenderTypeEnum.Male,
      idNumber: guest?.idNumber || '',
      name: guest?.name || '',
      phone: guest?.phone || '',
      phone_2: guest?.phone_2 || '',
      title: guest?.title || null,
    };
  }, [guest]);

  return defaultValues;
}
